/**
 * <BilingualText /> — read-only display of a paired EN/AR string.
 *
 * The locked rendering pattern from DESIGN_SYSTEM.md §10:
 *
 *   <div lang="en" dir="ltr">{en}</div>
 *   <div lang="ar" dir="rtl" class="text-sm text-text-secondary">{ar}</div>
 *
 * Each language is its own block element with explicit `lang` and
 * `dir` attributes so the browser's Unicode bidirectional (bidi)
 * algorithm handles each independently. The common bug we're fixing
 * is rendering Arabic inline inside an LTR cell without isolation —
 * the browser runs the entire cell through bidi-complex layout,
 * which collapses Arabic + Latin into a single line with whitespace
 * reordering that looks broken to the user.
 *
 * Always `text-align: start` (respects parent direction; never
 * `left` or `right`).
 *
 * Variants:
 *   - "stacked" (default) — English on top, Arabic block-level below.
 *     Right for table cells where a row has limited vertical space
 *     and the AR string is a translation of the EN primary.
 *   - "inline" — both on the same line, separated by a small ·,
 *     each wrapped in <bdi> so cursor-direction inside the cell
 *     stays sane. Use for short labels where vertical space is
 *     tight (badges, breadcrumb tail).
 *
 * Locale-aware swap: when the parent `dir` is `rtl` (Arabic UI),
 * the Arabic side rendered as the primary visual emphasis. This is
 * a visual swap of weight only — the DOM order is preserved so
 * screen readers always read English-then-Arabic consistently.
 */

import { useEffect, useState } from "react";

export interface BilingualTextProps {
  en: string | null | undefined;
  ar: string | null | undefined;
  layout?: "stacked" | "inline";
  /** Optional className applied to the wrapper. */
  className?: string;
  /** Tailwind class controlling the primary (top/leading) line. */
  primaryClassName?: string;
  /** Tailwind class controlling the secondary line. */
  secondaryClassName?: string;
  /** Hide the secondary line entirely (when only one language is present). */
  hideSecondary?: boolean;
}

function _useDocumentDir(): "ltr" | "rtl" {
  // Sync with <html dir> so the visual emphasis flips with locale
  // without us having to thread the locale prop everywhere.
  const initial =
    typeof document !== "undefined" && document.documentElement.dir === "rtl"
      ? "rtl"
      : "ltr";
  const [dir, setDir] = useState<"ltr" | "rtl">(initial);

  useEffect(() => {
    if (typeof document === "undefined") return;
    const sync = () =>
      setDir(document.documentElement.dir === "rtl" ? "rtl" : "ltr");
    // The locale switcher updates documentElement.dir; observe
    // attribute changes so this component re-renders.
    const observer = new MutationObserver((records) => {
      for (const r of records) {
        if (r.type === "attributes" && r.attributeName === "dir") {
          sync();
          break;
        }
      }
    });
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["dir"],
    });
    return () => observer.disconnect();
  }, []);

  return dir;
}

export function BilingualText({
  en,
  ar,
  layout = "stacked",
  className = "",
  primaryClassName,
  secondaryClassName,
  hideSecondary = false,
}: BilingualTextProps) {
  const dir = _useDocumentDir();
  const hasEn = !!en && en.trim() !== "";
  const hasAr = !!ar && ar.trim() !== "";

  if (!hasEn && !hasAr) return null;

  // Which side reads as "primary" depends on the current UI locale,
  // not the cell's intrinsic direction. In Arabic UI the AR string
  // carries the bold/larger styling; in English UI the EN does.
  const primaryLang: "en" | "ar" = dir === "rtl" ? "ar" : "en";
  const secondaryLang: "en" | "ar" = primaryLang === "en" ? "ar" : "en";

  const primaryValue = primaryLang === "en" ? en : ar;
  const secondaryValue = secondaryLang === "en" ? en : ar;
  const showSecondary =
    !hideSecondary &&
    !!secondaryValue &&
    (primaryLang === "en" ? hasAr : hasEn);

  const primaryDir = primaryLang === "ar" ? "rtl" : "ltr";
  const secondaryDir = secondaryLang === "ar" ? "rtl" : "ltr";

  if (layout === "inline") {
    return (
      <span
        className={`inline-flex items-baseline gap-1.5 ${className}`}
        style={{ textAlign: "start" }}
      >
        <bdi lang={primaryLang} dir={primaryDir} className={primaryClassName}>
          {primaryValue}
        </bdi>
        {showSecondary && (
          <>
            <span aria-hidden className="text-text-tertiary">
              ·
            </span>
            <bdi
              lang={secondaryLang}
              dir={secondaryDir}
              className={
                secondaryClassName ?? "text-xs text-text-secondary"
              }
            >
              {secondaryValue}
            </bdi>
          </>
        )}
      </span>
    );
  }

  return (
    <div
      className={`flex flex-col ${className}`}
      style={{ textAlign: "start" }}
    >
      <div
        lang={primaryLang}
        dir={primaryDir}
        className={primaryClassName}
        style={{ textAlign: "start" }}
      >
        {primaryValue}
      </div>
      {showSecondary && (
        <div
          lang={secondaryLang}
          dir={secondaryDir}
          className={
            secondaryClassName ?? "text-xs text-text-secondary"
          }
          style={{ textAlign: "start" }}
        >
          {secondaryValue}
        </div>
      )}
    </div>
  );
}
